import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, Folder, FolderOpen, AlertCircle, PackagePlus } from 'lucide-react';

interface CategoryNode {
  id: string;
  name: string;
  hsCode: string;
  productCount?: number;
  children: CategoryNode[];
}

const initialTree: CategoryNode = {
  id: 'root',
  name: 'Categories',
  hsCode: '',
  children: [
    {
      id: '1',
      name: 'قطعات الکترونیکی',
      hsCode: '8541',
      productCount: 120,
      children: [
        { 
          id: '1-1', 
          name: 'مقاومت', 
          hsCode: '8533', 
          productCount: 45,
          children: [
            { id: '1-1-1', name: 'کربنی', hsCode: '853310', productCount: 15, children: [] }
          ] 
        },
        { id: '1-2', name: 'خازن', hsCode: '8532', productCount: 30, children: [] }
      ]
    },
    {
      id: '2',
      name: 'تجهیزات جانبی',
      hsCode: '8544',
      productCount: 5,
      children: []
    }
  ]
};

// Tree helper functions
const isDuplicateName = (tree: CategoryNode, mode: 'add' | 'edit', targetId: string, newName: string): boolean => {
  if (mode === 'add') {
    if (tree.id === targetId) {
      return tree.children.some(child => child.name === newName);
    }
    return tree.children.some(child => isDuplicateName(child, mode, targetId, newName));
  } else {
    if (tree.children.some(child => child.id === targetId)) {
      return tree.children.some(child => child.id !== targetId && child.name === newName);
    }
    return tree.children.some(child => isDuplicateName(child, mode, targetId, newName));
  }
};

const addNode = (tree: CategoryNode, parentId: string, newNode: CategoryNode): CategoryNode => {
  if (tree.id === parentId) {
    return { ...tree, children: [...tree.children, newNode] };
  }
  return {
    ...tree,
    children: tree.children.map(child => addNode(child, parentId, newNode))
  };
};

const editNode = (tree: CategoryNode, id: string, name: string, hsCode: string): CategoryNode => {
  if (tree.id === id) {
    return { ...tree, name, hsCode };
  }
  return {
    ...tree,
    children: tree.children.map(child => editNode(child, id, name, hsCode))
  };
};

const deleteNode = (tree: CategoryNode, id: string): CategoryNode => {
  return {
    ...tree,
    children: tree.children
      .filter(child => child.id !== id)
      .map(child => deleteNode(child, id))
  };
};

interface CategoryItemProps {
  node: CategoryNode;
  level: number;
  onAdd: (parentId: string) => void;
  onEdit: (node: CategoryNode) => void;
  onDelete: (node: CategoryNode) => void;
  expandedIds: string[];
  toggleExpand: (id: string) => void;
  onNodeClick: (node: CategoryNode) => void;
  onAddProduct: (node: CategoryNode) => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({ node, level, onAdd, onEdit, onDelete, expandedIds, toggleExpand, onNodeClick, onAddProduct }) => {
  const isRoot = node.id === 'root';
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.includes(node.id);

  return (
    <div className="w-full">
      <div 
        className={`flex items-center justify-between py-2 px-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg group transition-colors ${
          isRoot ? 'font-bold bg-slate-50/50 dark:bg-slate-800/30' : ''
        }`}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        <div 
          className="flex items-center gap-2 cursor-pointer select-none flex-1" 
          onClick={() => {
            if (hasChildren) {
              toggleExpand(node.id);
            }
          }}
          onDoubleClick={() => {
            if (!hasChildren) {
              onNodeClick(node);
            }
          }}
        >
          <div className="w-5 flex items-center justify-center">
            {hasChildren && (
              isExpanded ? (
                <ChevronDown size={18} className="text-slate-400 dark:text-slate-500" />
              ) : (
                <ChevronRight size={18} className="text-slate-400 dark:text-slate-500" />
              )
            )}
          </div>
          
          {isExpanded ? (
            <FolderOpen size={18} className={isRoot ? "text-blue-500" : "text-amber-500"} fill={isRoot ? "#3b82f633" : "#f59e0b33"} />
          ) : (
            <Folder size={18} className={isRoot ? "text-blue-500" : "text-amber-500"} fill={isRoot ? "#3b82f633" : "#f59e0b33"} />
          )}
          
          <span className={`text-[14px] ${isRoot ? 'text-blue-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200'}`}>
            {node.name}
          </span>
          
          {!isRoot && (
            <span className="text-[11px] bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md ml-3">
              {node.productCount || 0} کالا
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-auto pr-2">
          {level < 4 ? (
            <button 
              onClick={(e) => { e.stopPropagation(); onAdd(node.id); }}
              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded"
              title="افزودن زیردسته"
            >
              <Plus size={16} />
            </button>
          ) : (
            <>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onAddProduct(node);
                }}
                className="px-2 py-1 flex items-center gap-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded"
                title="افزودن کالای جدید به این دسته‌بندی"
              >
                <Plus size={16} />
                <span className="text-[11px] font-medium hidden sm:inline">افزودن کالا</span>
              </button>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onNodeClick(node);
                }}
                className="px-2 py-1 flex items-center gap-1 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30 rounded"
                title="نمایش کالاهای این دسته‌بندی"
              >
                <PackagePlus size={16} />
                <span className="text-[11px] font-medium hidden sm:inline">نمایش کالاها</span>
              </button>
            </>
          )}
          {!isRoot && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); onEdit(node); }}
                className="p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded"
                title="ویرایش"
              >
                <Edit size={16} />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onDelete(node); }}
                className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 rounded"
                title="حذف"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            {node.children.map(child => (
              <CategoryItem 
                key={child.id}
                node={child}
                level={level + 1}
                onAdd={onAdd}
                onEdit={onEdit}
                onDelete={onDelete}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
                onNodeClick={onNodeClick}
                onAddProduct={onAddProduct}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Categories = () => {
  const navigate = useNavigate();
  const [tree, setTree] = useState<CategoryNode>(initialTree);
  const [expandedIds, setExpandedIds] = useState<string[]>(['root', '1']);
  
  // Modals state
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [targetId, setTargetId] = useState<string>(''); // parentId for add, nodeId for edit
  const [formData, setFormData] = useState({ name: '', hsCode: '' });
  
  // Alerts state
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const [confirmMsg, setConfirmMsg] = useState<{message: string, onConfirm: () => void} | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleNodeClick = (node: CategoryNode) => {
    navigate('/admin/products', { state: { category: node.name } });
  };

  const handleAddProduct = (node: CategoryNode) => {
    navigate('/admin/products/new', { state: { category: node.name } });
  };

  const handleAdd = (parentId: string) => {
    setTargetId(parentId);
    setFormData({ name: '', hsCode: '' });
    setModalMode('add');
    if (!expandedIds.includes(parentId)) {
      setExpandedIds(prev => [...prev, parentId]);
    }
  };

  const handleEdit = (node: CategoryNode) => {
    setTargetId(node.id);
    setFormData({ name: node.name, hsCode: node.hsCode });
    setModalMode('edit');
  };

  const handleDelete = (node: CategoryNode) => {
    if (node.children.length > 0) {
      setAlertMsg("این دسته دارای زیر مجموعه است و امکان حذف آن وجود ندارد.");
      return;
    }
    
    setConfirmMsg({
      message: `آیا از حذف دسته "${node.name}" اطمینان دارید؟`,
      onConfirm: () => {
        setTree(prev => deleteNode(prev, node.id));
        setConfirmMsg(null);
      }
    });
  };

  const submitModal = () => {
    if (!formData.name.trim()) return;
    
    if (modalMode && isDuplicateName(tree, modalMode, targetId, formData.name.trim())) {
      setAlertMsg("دسته‌ای با این نام در این زیرمجموعه از قبل وجود دارد.");
      return;
    }
    
    if (modalMode === 'add') {
      const newNode: CategoryNode = {
        id: Math.random().toString(36).substr(2, 9),
        name: formData.name,
        hsCode: formData.hsCode,
        productCount: 0,
        children: []
      };
      setTree(prev => addNode(prev, targetId, newNode));
    } else if (modalMode === 'edit') {
      setTree(prev => editNode(prev, targetId, formData.name, formData.hsCode));
    }
    
    setModalMode(null);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col h-[calc(100vh-8rem)] text-slate-800 dark:text-slate-200 relative">
      <div className="overflow-auto flex-1 bg-white dark:bg-slate-900 p-4" dir="ltr">
        <div className="max-w-3xl border border-slate-100 dark:border-slate-800/80 rounded-xl p-2 bg-white dark:bg-slate-800/50">
          <CategoryItem 
            node={tree} 
            level={0} 
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            onNodeClick={handleNodeClick}
            onAddProduct={handleAddProduct}
          />
        </div>
      </div>

      {/* Edit/Add Modal */}
      <AnimatePresence>
        {modalMode && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm overflow-hidden"
            >
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
                <h4 className="font-bold text-slate-800 dark:text-white">
                  {modalMode === 'add' ? 'افزودن دسته‌بندی جدید' : 'ویرایش دسته‌بندی'}
                </h4>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    نام دسته‌بندی
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:text-white"
                    placeholder="مثال: مقاومت‌ها"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    HS Code <span className="text-slate-400 text-xs font-normal">(اختیاری)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.hsCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, hsCode: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:text-white text-left"
                    placeholder="مثال: 8533"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end bg-slate-50 dark:bg-slate-800/80">
                <button
                  onClick={() => setModalMode(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  انصراف
                </button>
                <button
                  onClick={submitModal}
                  disabled={!formData.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  ذخیره
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Alert Modal */}
      <AnimatePresence>
        {alertMsg && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm overflow-hidden"
            >
              <div className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-4">
                  <AlertCircle size={24} />
                </div>
                <h4 className="font-bold text-slate-800 dark:text-white text-lg mb-2">امکان انجام این عملیات نیست</h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{alertMsg}</p>
              </div>
              <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex justify-center bg-slate-50 dark:bg-slate-800/80">
                <button
                  onClick={() => setAlertMsg(null)}
                  className="px-8 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors w-full"
                >
                  متوجه شدم
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirm Modal */}
      <AnimatePresence>
        {confirmMsg && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm overflow-hidden"
            >
              <div className="p-6">
                <h4 className="font-bold text-slate-800 dark:text-white mb-2">تایید عملیات</h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{confirmMsg.message}</p>
              </div>
              <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end bg-slate-50 dark:bg-slate-800/80">
                <button
                  onClick={() => setConfirmMsg(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  انصراف
                </button>
                <button
                  onClick={confirmMsg.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  بله، حذف کن
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
